/**
 * billingConfig — the one opaque, price-agnostic constant the checkout UI
 * needs and cannot discover from the API.
 *
 * WHY THIS EXISTS (a documented deviation — see the F14-PR3 task return for
 * the full writeup): `POST /billing/checkout-session` requires a `price_id`
 * that must exactly match an entry in the backend's `BILLING_PRICE_IDS`
 * config (backend/app/config.py `billing_price_config()`). Neither PR1 nor
 * PR2 shipped an endpoint that LISTS purchasable prices — `billing.py` has
 * exactly five routes (webhook, checkout-session, portal-session,
 * entitlement, activation), none of which enumerate `BILLING_PRICE_IDS`. The
 * PRD's architecture prose ("lists purchasable options by querying Stripe")
 * describes a capability that was never built as a shipped endpoint.
 *
 * Rather than invent a fake id or block the whole checkout surface on a
 * backend change outside this PR's scope, the frontend mirrors its OWN
 * `API_URL` pattern (config.js) with a build-time env var: ops sets
 * `REACT_APP_BILLING_PRICE_ID` to match ONE entry in the backend's
 * `BILLING_PRICE_IDS` when they create the Stripe price in the dashboard.
 * This stays price-agnostic in the sense the PRD means it — the value is an
 * OPAQUE POINTER (`price_...`), never a dollar amount, product name, or
 * literal baked into source — exactly like `BILLING_PRICE_IDS` itself is
 * described ("no AMOUNT ever appears here — the price id is an opaque
 * pointer into the dashboard").
 *
 * Since there is only ONE entitlement tier today (`dsat_premium`, the PRD's
 * "single v1 tier"), a single default id is sufficient; a multi-price
 * picker (monthly vs. annual, etc.) is out of scope until PR4's pricing
 * memo and `BILLING_PRICE_IDS` actually carries more than one live entry.
 *
 * VITE WIRING (load-bearing): Vite's `define` does a literal per-key text
 * substitution — it does NOT polyfill `process.env` generally — so this
 * exact key has a matching entry in `vite.config.js`'s `define` block
 * (mirrors `REACT_APP_API_URL` / `REACT_APP_LESSONS_ENABLED`). Without that
 * entry this reference would crash at runtime (`process is not defined`)
 * under the real bundler, invisible to Jest (which runs under plain Node,
 * where `process.env` always exists).
 *
 * Read through a FUNCTION, not a module-level const, matching
 * featureFlags.js's convention — tests can drive it via `process.env`
 * directly (see billingConfig.test.js) without reaching for
 * `jest.mock(this module)`.
 *
 * Every consumer MUST treat '' (unset) as "nothing purchasable configured
 * yet" and render a calm empty state instead of attempting a checkout call
 * — an empty `price_id` fails Pydantic's `min_length=1` as a generic 422,
 * which is a worse signal to build UX around than a deliberate check here.
 */

/** The single purchasable price id for v1, or '' when not yet configured. */
export function defaultPriceId() {
  return process.env.REACT_APP_BILLING_PRICE_ID || '';
}
